/**
 * Utility para manipulação de imagens no frontend
 */

/**
 * Redimensionar imagem para largura máxima mantendo proporção
 * @param {File} file - Arquivo de imagem
 * @param {number} maxWidth - Largura máxima (padrão 640px)
 * @param {number} quality - Qualidade JPEG (0-1, padrão 0.85)
 * @returns {Promise<string>} - Base64 da imagem redimensionada
 */
export const resizeImage = (file, maxWidth = 640, quality = 0.85) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calcular novas dimensões
      let { width, height } = img;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      // Configurar canvas
      canvas.width = width;
      canvas.height = height;

      // Desenhar imagem redimensionada
      ctx.drawImage(img, 0, 0, width, height);

      // Converter para base64
      const base64 = canvas.toDataURL('image/jpeg', quality);
      resolve(base64);
    };

    img.onerror = () => {
      reject(new Error('Erro ao carregar imagem'));
    };

    // Carregar imagem
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.onerror = () => {
      reject(new Error('Erro ao ler arquivo'));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Validar arquivo de imagem
 * @param {File} file - Arquivo a ser validado
 * @param {number} maxSizeMB - Tamanho máximo em MB (padrão 10MB)
 * @returns {object} - {valid: boolean, error?: string}
 */
export const validateImageFile = (file, maxSizeMB = 10) => {
  // Verificar se é um arquivo
  if (!file) {
    return { valid: false, error: 'Nenhum arquivo selecionado' };
  }

  // Verificar tipo de arquivo
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    return { 
      valid: false, 
      error: 'Tipo de arquivo não suportado. Use JPEG, PNG, GIF ou WebP' 
    };
  }

  // Verificar tamanho
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return { 
      valid: false, 
      error: `Arquivo muito grande. Tamanho máximo: ${maxSizeMB}MB` 
    };
  }

  return { valid: true };
};

/**
 * Obter informações da imagem
 * @param {File} file - Arquivo de imagem
 * @returns {Promise<object>} - {width, height, size, type}
 */
export const getImageInfo = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
        size: file.size,
        type: file.type,
        name: file.name
      });
    };

    img.onerror = () => {
      reject(new Error('Erro ao carregar imagem'));
    };

    // Carregar imagem
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.onerror = () => {
      reject(new Error('Erro ao ler arquivo'));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Formatar tamanho em bytes para string legível
 * @param {number} bytes - Tamanho em bytes
 * @returns {string} - Tamanho formatado (ex: "1.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Criar preview da imagem
 * @param {File} file - Arquivo de imagem
 * @returns {Promise<string>} - URL da imagem para preview
 */
export const createImagePreview = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      resolve(e.target.result);
    };
    
    reader.onerror = () => {
      reject(new Error('Erro ao criar preview da imagem'));
    };
    
    reader.readAsDataURL(file);
  });
};
